import atlassian from './_assets/atlassian.png';
import components from './_assets/components.png';
import confluence from './_assets/confluence.png';
import content from './_assets/content.png';
import figmaLibraries from './_assets/figma-libraries.png';
import forge from './_assets/forge.png';
import foundations from './_assets/foundations.png';
import getstarted from './_assets/get-started.png';
import tokens from './_assets/tokens.png';
import trainingA11y from './_assets/training-a11y.png';
import trainingADS from './_assets/training-ads.png';
import trainingFigma from './_assets/training-figma.png';
import usingTokens from './_assets/using-tokens.png';
import { type GuidanceCard } from './guidance-cards';

export const guidanceCardGetStarted: GuidanceCard = {
	image: getstarted,
	title: 'Get started',
	description:
		'Start designing and developing beautiful interfaces with Atlassian Design System foundations, components, and standards.',
	href: '/get-started',
};

export const guidanceCardFoundations: GuidanceCard = {
	image: foundations,
	title: 'Foundations',
	description:
		'Create engaging user experiences with foundations. These include our tokens, guidelines, and visual styles: color, spacing, typography, and more.',
	href: '/foundations',
};

export const guidanceCardComponents: GuidanceCard = {
	image: components,
	title: 'Components',
	description:
		'Combine reusable building blocks to meet specific interaction needs and create consistent user experiences.',
	href: '/components',
};

export const guidanceCardTokens: GuidanceCard = {
	image: tokens,
	title: 'Tokens',
	description:
		'Design tokens are the single source of truth to name and store decisions about the user interface, such as colors, typography, and spacing.',
	href: '/foundations/tokens',
};

export const guidanceCardContent: GuidanceCard = {
	image: content,
	backgroundColor: 'elevation.surface.sunken',
	title: 'Content',
	description:
		'Follow content guidance on our voice and tone, language and grammar, inclusive language, Atlassian vocabulary, and date and time.',
	href: '/foundations/content',
};

export const guidanceCardFigmaLibrariesInternal: GuidanceCard = {
	image: confluence,
	backgroundColor: 'color.background.accent.blue.subtlest',
	title: 'Figma libraries with design tokens',
	description: 'Browse up-to-date libraries for components, design tokens, foundations, and more.',
	href: 'https://go.atlassian.com/figma-libraries',
	tag: 'Atlassians only',
};

export const guidanceCardFigmaPluginInternal: GuidanceCard = {
	image: atlassian,
	backgroundColor: 'color.background.accent.blue.subtlest',
	title: 'Atlassian design tokens Figma plugin',
	description:
		'Apply design tokens, swap designs to dark mode, and migrate old Atlassian colors to tokens.',
	href: 'https://www.figma.com/community/plugin/1144189860740169808/atlassian-design-tokens',
	tag: 'Atlassians only',
};

export const guidanceCardAccessibilityAnnotations: GuidanceCard = {
	image: confluence,
	backgroundColor: 'color.background.accent.blue.subtlest',
	title: 'Accessibility annotations library',
	description:
		'Communicate the functionality, hierarchy, and behavior of elements in designs to engineering partners with accessibility annotations.',
	href: 'https://go.atlassian.com/figma-a11y-annotation',
	tag: 'Atlassians only',
};

export const guidanceCardFigmaLibrariesExternal: GuidanceCard = {
	image: atlassian,
	backgroundColor: 'color.background.accent.blue.subtlest',
	title: 'Figma Community libraries',
	description:
		'Access public design system Figma libraries, plugins, and accessibility annotations.',
	href: 'https://www.figma.com/@atlassian',
	tag: 'Public',
};

export const guidanceCardFigmaLibrariesPage: GuidanceCard = {
	image: figmaLibraries,
	title: 'Figma libraries',
	description: 'Access the latest libraries for components, foundations, tokens, and styles.',
	href: '/get-started/figma-libraries',
};

export const guidanceCardUsingTokensPage: GuidanceCard = {
	image: usingTokens,
	backgroundColor: 'elevation.surface.sunken',
	title: 'Using tokens in design',
	description: 'Design using tokens in Figma and preview new colors and tokens in apps.',
	href: '/tokens/use-tokens-in-design',
};

export const guidanceCardDesignHub: GuidanceCard = {
	image: confluence,
	backgroundColor: 'color.background.accent.blue.subtlest',
	title: 'Design Hub',
	description: 'Explore the Design Hub for practices, rituals, training and resources.',
	href: 'https://go.atlassian.com/design-hub',
	tag: 'Atlassians only',
};

export const guidanceCardAssetLibrary: GuidanceCard = {
	image: atlassian,
	backgroundColor: 'color.background.accent.blue.subtlest',
	title: 'Asset library',
	description:
		'Access logos and brand guidelines. Log in to download fonts, illustrations, presentation kit, and templates.',
	href: 'https://orangedam.atlassian.com/',
};

export const guidanceCardTrainingFigma: GuidanceCard = {
	image: trainingFigma,
	backgroundColor: 'color.background.accent.purple.subtlest',
	title: 'Figma at Atlassian',
	description: 'Obtain licenses, and create basic prototypes with interactive components.',
	href: 'https://atlassian.myabsorb.com/#/online-courses/1ded0c99-ef35-47c5-ae8b-e100ba7cd119',
	tag: 'Atlassians only',
};

export const guidanceCardTrainingADS: GuidanceCard = {
	image: trainingADS,
	backgroundColor: 'color.background.accent.blue.subtlest',
	title: 'The Atlassian Design System',
	description: 'Understand the Atlassian Design System and how it can benefit you and your team.',
	href: 'https://atlassian.myabsorb.com/#/online-courses/6bd017ad-df5f-4766-b8bd-38d5424d089f',
	tag: 'Atlassians only',
};

export const guidanceCardTrainingA11y: GuidanceCard = {
	image: trainingA11y,
	backgroundColor: 'color.background.accent.orange.subtlest',
	title: 'Accessible content design',
	description:
		'Learn about writing accessible content, inclusive language, and testing content for accessibility.',
	href: 'https://atlassian.myabsorb.com/#/online-courses/2de76f45-aadf-48b1-80b7-1509de53edeb',
	tag: 'Atlassians only',
};

export const guidanceCardForge: GuidanceCard = {
	image: forge,
	title: 'Forge UI Kit components',
	description: `UI Kit offers a wide range of pre-built and customizable components that align with Atlassian's design standards.`,
	href: 'https://developer.atlassian.com/platform/forge/ui-kit/components/',
	tag: 'Public',
};

export const guidanceCardDesigningMessages: GuidanceCard = {
	image: content,
	title: 'Designing messages',
	description:
		'Browse this section for information on choosing the right component, icon, and color for effective messages.',
	href: '/foundations/content/designing-messages',
};

export const guidanceCardWritingToolkit: GuidanceCard = {
	image: forge,
	title: 'Atlassian developer writing toolkit',
	description:
		'Write and publish developer content for your app or service on the Atlassian developer documentation platform (DAC).',
	href: 'https://developer.atlassian.com/platform/writing-toolkit/',
	tag: 'Atlassians only',
};

export const guidanceCardSAC: GuidanceCard = {
	image: confluence,
	title: 'SAC user documentation standards',
	description:
		'Access resources and apply standards when creating support documentation for Atlassian Support (SAC).',
	href: 'https://go.atlassian.com/sac-standards',
	tag: 'Atlassians only',
};

export const guidanceCardAiLibrariesInternal: GuidanceCard = {
	image: figmaLibraries,
	backgroundColor: 'color.background.accent.purple.subtlest',
	title: 'AI design patterns in Figma',
	description: 'Browse components, tokens, and patterns for Rovo and AI designs.',
	href: 'https://go.atlassian.com/ai-figma-libraries',
	tag: 'Atlassians only',
};
